/**
 * Signing out, asked for by the Tab Service portal.
 *
 * Somebody who signed out of the portal is signed out of e-filing too. The portal sends the
 * browser here with the address to come back to; this page ends the session on this domain
 * and returns. The return address is honoured only when it points at the portal, so the page
 * cannot be used to bounce anyone anywhere else.
 *
 * Nothing is shown for long: the session is gone before the first paint finishes.
 */
import { useEffect, useState } from 'react';
import { Link, createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { authStore } from '@/lib/auth-store';
import { HandoverScreen } from '@/components/handover-screen';
import { portalLoginUrl, safePortalReturn } from '@/lib/session-exit';

interface SignOutSearch {
  return?: string;
}

export const Route = createFileRoute('/sign-out')({
  validateSearch: (search: Record<string, unknown>): SignOutSearch => ({
    return: typeof search.return === 'string' ? search.return : undefined,
  }),
  component: SignOutPage,
  // The default pending state is a page skeleton. On this route that is a screenful of grey
  // blocks announcing that something is loading while somebody leaves. Nothing is drawn.
  pendingComponent: () => null,
});

/** How long the browser gets to leave before anything is drawn. */
const STUCK_AFTER_MS = 6000;

function SignOutPage() {
  const navigate = useNavigate();
  const { queryClient } = Route.useRouteContext();
  const search = useSearch({ from: '/sign-out' });
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let stuckTimer: number | undefined;
    (async () => {
      await authStore.signOut().catch(() => undefined);
      // Whatever was fetched belonged to the session that just ended.
      queryClient.clear();
      if (cancelled) return;
      const back = safePortalReturn(search.return) ?? portalLoginUrl();
      if (back) {
        window.location.replace(back);
        // The browser is leaving, but leaving is not instant and setting this straight away
        // painted "You are signed out" over the wait - the very announcement this page exists
        // to avoid. The way out by hand appears only once the browser has plainly not gone.
        stuckTimer = window.setTimeout(() => setStuck(true), STUCK_AFTER_MS);
        return;
      }
      await navigate({ to: '/sign-in', replace: true });
    })();
    return () => {
      cancelled = true;
      if (stuckTimer) window.clearTimeout(stuckTimer);
    };
  }, [navigate, queryClient, search.return]);

  // Nothing is drawn while it works. Signing out crosses to the portal and back, and anything
  // painted here announces that crossing, which is what somebody leaving least needs to see.
  // The way out by hand appears only if the browser somehow stays put.
  if (!stuck) return null;

  return (
    <HandoverScreen title="You are signed out">
      <Button asChild variant="primary" size="lg" className="w-full">
        <Link to="/sign-in">Sign in</Link>
      </Button>
    </HandoverScreen>
  );
}
